/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Grid, Link, typography2 } from '@coursera/cds-core';
import type { PageTabName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

export type Props = {
  trackingName?: PageTabName;
  label?: string;
  href?: string;
  isCurrent?: boolean;
  name?: string;
  isButton?: boolean;
};

const styles = {
  link: css`
    // chaining the selectors to override CDS styles
    &&& {
      display: block;
      white-space: nowrap;
      padding: var(--cds-spacing-100);
      color: var(--cds-color-neutral-primary-weak);
      text-decoration: none;
      ${typography2.bodySecondary}

      :hover {
        color: var(--cds-color-interactive-primary);
      }

      :focus {
        outline: 5px auto -webkit-focus-ring-color;
        outline-offset: -2px;

        /* override box-shadow from .cds-link-focusVisible */
        box-shadow: none !important;
      }
    }
  `,
  button: css`
    &.isCurrent {
      color: var(--cds-color-interactive-primary-hover);
    }

    ${typography2.bodySecondary}
    color: var(--cds-color-neutral-primary-weak);

    :hover,
    :focus,
    :active {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      text-decoration: none;
      color: var(--cds-color-interactive-primary-hover);
    }
  `,
  currentLink: css`
    font-weight: bold;
    color: var(--cds-color-interactive-primary);
    border-bottom: 4px solid var(--cds-color-interactive-primary);
  `,
};

const NavLinkGridItem = ({ label, href, isCurrent, name, trackingName, isButton }: Props) => {
  const track = useTracker();

  if (!isButton) {
    return (
      <Grid item>
        <Link
          href={href}
          variant="quiet"
          css={[styles.link, isCurrent && styles.currentLink]}
          {...(isCurrent ? { 'aria-current': 'page' } : {})}
          data-testid={`nav-link-grid-item-${name ?? href}`}
          onClick={() => {
            track('click_page_tab', { pageTab: { name: trackingName } });
          }}
        >
          {label}
        </Link>
      </Grid>
    );
  } else {
    return (
      <Grid item>
        <Button
          href={href}
          variant="ghost"
          css={[styles.link]}
          data-testid={`nav-link-grid-item-${name ?? href}`}
          onClick={() => {
            track('click_page_tab', { pageTab: { name: trackingName } });
          }}
          component={Link}
          size="small"
          className={isCurrent ? 'isCurrent' : undefined}
          aria-current={isCurrent ? 'page' : undefined}
        >
          {label}
        </Button>
      </Grid>
    );
  }
};

export default NavLinkGridItem;
